import React from 'react';
import classNames from 'classnames';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import tinyColor from 'tinycolor2';

const minwrap = [];

const defaultRenderTemplate = item => {
  return (
    <div className="swim-card">
      <div>{item.title}</div>
      <div>{item.description}</div>
      <div>{item.assignee}</div>
    </div>
  );
};

const getSwimLaneBgColor = (primaryColor = '#000000') => {
  // If it's white then you won't see the color
  const determineColor = tinyColor(primaryColor).getLuminance() * 100 > 95 ? '#000' : primaryColor;
  return tinyColor(determineColor).setAlpha(0.1).toRgbString();
};

const getSwimLaneBorderColor = (primaryColor = '#000000') => {
  // If it's white then you won't see the color
  const determineColor = tinyColor(primaryColor).getLuminance() * 100 > 95 ? '#000' : primaryColor;
  return tinyColor(determineColor).toRgbString();
};

const onDragEnd = (result, setColumnItems, columnItems, onChange) => {
  if (!result.destination) {
    return;
  }

  const { source, destination } = result;

  if (source.droppableId !== destination.droppableId) {
    const sourceColumn = columnItems[source.droppableId];
    const destColumn = columnItems[destination.droppableId];
    const sourceItems = [...sourceColumn];
    const destItems = [...destColumn];

    // Remove from source swim lane
    const [removed] = sourceItems.splice(source.index, 1);

    // Add to des swimlane
    destItems.splice(destination.index, 0, removed);

    const newColumns = {
      ...columnItems,
      [source.droppableId]: sourceItems,
      [destination.droppableId]: destItems,
    };

    setColumnItems(newColumns);

    if (onChange) {
      onChange(source, destination, removed, newColumns);
    }

    return;
  }

  // Needs doc
  const column = columnItems[source.droppableId];
  const copiedItems = [...column];
  const [removed] = copiedItems.splice(source.index, 1);
  copiedItems.splice(destination.index, 0, removed);
  setColumnItems({
    ...columnItems,
    [source.droppableId]: copiedItems,
  });
};

const KanbanBoard = ({ columns, setColumnItems, columnItems, config, setConfig, renderTemplate = defaultRenderTemplate, onChange }) => {
  const minimize = id => {
    setConfig({
      ...config,
      [id]: {
        ...config[id],
        minimized: true,
      },
    });
  };

  const restore = id => {
    setConfig({
      ...config,
      [id]: {
        ...config[id],
        minimized: false,
      },
    });
  };

  minwrap.splice(0, minwrap.length);

  const open = columnItems[0];

  return (
    <div className="kanban-board">
      <DragDropContext onDragEnd={result => onDragEnd(result, setColumnItems, columnItems, onChange)}>
        <div className="swim-lanes">
          {columns.map(column => {
            const { id } = column;
            const items = columnItems[id] || [];

            if (column.name === 'Open') {
              return null;
            }

            if (config[id] && config[id].minimized) {
              minwrap.push(
                <React.Fragment key={`min-${column}-${id}`}>
                  <div className="swim-lane-minimized" onClick={() => restore(id)}>
                    <Droppable droppableId={id} direction="horizontal">
                      {(provided, snapshot) => {
                        minwrap.push(id);
                        return (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className={classNames('swim-lane-droppable', {
                              'drag-over': snapshot.isDraggingOver,
                            })}
                            style={{
                              color: tinyColor(column.color).isDark() ? '#fff' : '#333',
                              backgroundColor: column.color,
                            }}
                          >
                            <div className="minimized-count">{items.length}</div>
                            <div className="minimized-name">{column.name}</div>
                          </div>
                        );
                      }}
                    </Droppable>
                  </div>
                  <div style={{ height: '25px' }}>.</div>
                </React.Fragment>
              );
              return null;
            }

            const minimizedElements = minwrap.length ? (
              <div className={classNames('minimized-elements', { single: minwrap.length === 1 })}>
                {minwrap.map(e => e)}
              </div>
            ) : null;

            minwrap.splice(0, minwrap.length);

            return (
              <React.Fragment key={`${column}-${id}`}>
                {minimizedElements}
                <div className="swim-lane">
                  <div className="swim-lane-header">
                    <div className="swim-lane-toolbar">
                      <div className="swim-button" onClick={() => minimize(id)}>
                        <i className="fa-solid fa-arrow-left-to-line" />
                      </div>
                    </div>
                    <div className="swim-lane-title">{column.name}</div>
                  </div>
                  <Droppable droppableId={id}>
                    {(provided, snapshot) => {
                      return (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className={classNames('swim-lane-droppable', {
                            'drag-over': snapshot.isDraggingOver,
                            minimized: config[id],
                          })}
                          style={{
                            borderColor: getSwimLaneBorderColor(column.color),
                            backgroundColor: getSwimLaneBgColor(column.color),
                          }}
                        >
                          {items.length === 0 && (
                            <div className="empty-wrapper">
                              <i className="fa-solid fa-square-e" />
                            </div>
                          )}
                          {items.map((item, index) => {
                            return (
                              <Draggable key={item.id} draggableId={item.id} index={index}>
                                {(provided, snapshot) => {
                                  return (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={classNames('request-card', {
                                        dragging: snapshot.isDragging,
                                        found: item.found === true,
                                      })}
                                      style={{
                                        ...provided.draggableProps.style,
                                      }}
                                    >
                                      {renderTemplate(item)}
                                    </div>
                                  );
                                }}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder.name}
                        </div>
                      );
                    }}
                  </Droppable>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="open-lane">
          <Droppable droppableId="0" direction="horizontal">
            {(provided, snapshot) => {
              return (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={classNames('swim-lane-droppable', {
                    'drag-over': snapshot.isDraggingOver,
                  })}
                >
                  {open.map((item, index) => {
                    return (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(provided, snapshot) => {
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={classNames('request-card', {
                                dragging: snapshot.isDragging,
                                found: item.found === true,
                              })}
                              style={{
                                ...provided.draggableProps.style,
                              }}
                            >
                              {renderTemplate(item)}
                            </div>
                          );
                        }}
                      </Draggable>
                    );
                  })}
                </div>
              );
            }}
          </Droppable>
        </div>
      </DragDropContext>
    </div>
  );
};

export default KanbanBoard;
